import React, { useState, useEffect, useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer 
} from 'recharts';
import { FiBarChart2, FiTrendingUp } from 'react-icons/fi';
import userRoleUtils from '../utils/userRoleUtils';
import apiService from '../utils/apiInterceptor';
import { dashboardRange, previousDashboardRange, dashboardSeries, dashboardTotals, localDate } from '../utils/dashboardPeriods';

const DashboardChart = ({ orders = [] }) => {
  const [chartType, setChartType] = useState('line');
  const [timeFilter, setTimeFilter] = useState('week');
  const [anchorDate, setAnchorDate] = useState(() => dashboardRange('day').start);
  const [range, setRange] = useState(() => dashboardRange('week'));
  const [loadedOrders, setLoadedOrders] = useState(orders);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const canViewComparisons = userRoleUtils.canViewDashboardComparisons();
  const previousRange = useMemo(() => previousDashboardRange(range, timeFilter), [range, timeFilter]);
  const validRange = Boolean(range.start && range.end && range.start <= range.end);

  useEffect(() => {
    let active = true;
    if (!validRange) return () => { active = false; };
    setLoading(true);
    setError(false);
    apiService.get(`/admin/dashboard/chart-orders?start_date=${previousRange.start}&end_date=${range.end}`)
      .then(response => { if (active) setLoadedOrders(response.orders || []); })
      .catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [previousRange.start, range.end, validRange, retry]);

  const selectPeriod = (period) => {
    setTimeFilter(period);
    if (period !== 'custom') setRange(dashboardRange(period, localDate(anchorDate)));
  };
  const selectDate = (field, value) => {
    if (!value) return;
    if (timeFilter !== 'custom') {
      setAnchorDate(value);
      setRange(dashboardRange(timeFilter, localDate(value)));
    } else {
      if (field === 'start') setAnchorDate(value);
      setRange(current => ({...current, [field]: value}));
    }
  };
  const chartData = useMemo(() => validRange ? dashboardSeries(loadedOrders, range, timeFilter) : [], [loadedOrders, range, timeFilter, validRange]);
  const comparison = useMemo(() => {
    const current = dashboardTotals(loadedOrders, range);
    const previous = dashboardTotals(loadedOrders, previousRange);
    const change = (value, oldValue) => oldValue === 0 ? (value === 0 ? 0 : 100) : ((value - oldValue) / oldValue) * 100;
    return {current, previous, salesChange: change(current.sales, previous.sales), ordersChange: change(current.orders, previous.orders)};
  }, [loadedOrders, range, previousRange]);

  const ComparisonCard = ({ title, current, previous, change, currency = false }) => {
    const increased = change >= 0;
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="text-sm font-semibold text-gray-500">{title}</div>
        <div className="mt-2 flex items-end justify-between gap-4">
          <div>
            <div className="text-2xl font-bold text-golden-navy">
              {currency ? `£${current.toFixed(2)}` : current}
            </div>
            <div className="text-xs text-gray-500">
              Previous: {currency ? `£${previous.toFixed(2)}` : previous}
            </div>
          </div>
          <div className={`text-lg font-bold ${increased ? 'text-green-600' : 'text-red-600'}`}>
            {increased ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg">
      <div className="flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-center mb-4">
        <h2 className="text-xl font-bold text-golden-navy">
          Order Value & Orders
        </h2>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2">
            <button
              onClick={() => setChartType('line')}
              className={`flex items-center px-4 py-2 rounded-lg ${
                chartType === 'line' 
                  ? 'bg-golden-navy text-white' 
                  : 'bg-gray-100'
              }`}
            >
              <FiTrendingUp className="mr-2" /> Line
            </button>
            <button
              onClick={() => setChartType('bar')}
              className={`flex items-center px-4 py-2 rounded-lg ${
                chartType === 'bar' 
                  ? 'bg-golden-navy text-white' 
                  : 'bg-gray-100'
              }`}
            >
              <FiBarChart2 className="mr-2" /> Bar
            </button>
          </div>

          <div className="flex flex-wrap bg-golden-navy rounded-lg overflow-hidden">
            <button
              onClick={() => selectPeriod('day')}
              className={`px-4 py-2 ${
                timeFilter === 'day' ? 'bg-white text-golden-navy' : 'text-white'
              }`}
            >
              Day
            </button>
            <button
              onClick={() => selectPeriod('week')}
              aria-pressed={timeFilter === 'week'}
              className={`px-4 py-2 ${
                timeFilter === 'week' ? 'bg-white text-golden-navy' : 'text-white'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => selectPeriod('month')}
              className={`px-4 py-2 ${
                timeFilter === 'month' ? 'bg-white text-golden-navy' : 'text-white'
              }`}
            >
              Month
            </button>
            <button
              onClick={() => selectPeriod('year')}
              className={`px-4 py-2 ${
                timeFilter === 'year' ? 'bg-white text-golden-navy' : 'text-white'
              }`}
            >
              Year
            </button>
            <button
              onClick={() => selectPeriod('custom')}
              aria-pressed={timeFilter === 'custom'}
              className={`px-4 py-2 ${timeFilter === 'custom' ? 'bg-white text-golden-navy' : 'text-white'}`}
            >
              Custom
            </button>
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm font-medium">From
          <input aria-label="Chart start date" type="date" value={range.start} onChange={event => selectDate('start', event.target.value)} className="ml-2 rounded border p-2" />
        </label>
        <label className="text-sm font-medium">To
          <input aria-label="Chart end date" type="date" value={range.end} onChange={event => selectDate('end', event.target.value)} className="ml-2 rounded border p-2" />
        </label>
        <button className="rounded border px-3 py-2 text-sm" onClick={() => { setAnchorDate(dashboardRange('day').start); setTimeFilter('week'); setRange(dashboardRange('week')); }}>Current week</button>
      </div>
      {timeFilter !== 'custom' && <p className="mb-2 text-sm text-gray-500">
        Choose any date in the {timeFilter} to show the full {timeFilter}{timeFilter === 'week' ? ' (Sunday–Saturday)' : ''}. Use Custom to set a From/To range.
      </p>}
      {!validRange ? <p role="alert">Choose an end date on or after the start date.</p> : (
        <p className="mb-4 text-sm text-gray-500">Selected: {range.start} – {range.end}. Comparing with {previousRange.start} – {previousRange.end}.</p>
      )}
      {loading && <p role="status">Loading selected period…</p>}
      {error && <p role="alert">Unable to load this period. <button className="underline" onClick={() => setRetry(value => value + 1)}>Retry</button></p>}
      {validRange && !loading && !error && <>
      <div className="w-full h-[360px]">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" tickFormatter={(value) => `£${value}`} />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === "Orders") return `${value} orders`;
                  return `£${value.toFixed(2)}`;
                }}
              />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="value" name="Order Value" stroke="#1e3a8a" />
              <Line yAxisId="right" type="monotone" dataKey="orders" name="Orders" stroke="#fbbf24" />
            </LineChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis yAxisId="left" orientation="left" tickFormatter={(value) => `£${value}`} />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === "Orders") return `${value} orders`;
                  return `£${value.toFixed(2)}`;
                }}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="value" name="Order Value" fill="#1e3a8a" />
              <Bar yAxisId="right" dataKey="orders" name="Orders" fill="#fbbf24" />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
      {canViewComparisons ? (
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
          <ComparisonCard
            title="Sales comparison"
            current={comparison.current.sales}
            previous={comparison.previous.sales}
            change={comparison.salesChange}
            currency
          />
          <ComparisonCard
            title="Order count comparison"
            current={comparison.current.orders}
            previous={comparison.previous.orders}
            change={comparison.ordersChange}
          />
        </div>
      ) : (
        <div className="mt-6">
          <ComparisonCard
            title="Total comparison"
            current={comparison.current.sales}
            previous={comparison.previous.sales}
            change={comparison.salesChange}
            currency
          />
        </div>
      )}
      </>}
    </div>
  );
};

export default DashboardChart;
